/**
 * RefundList Component - Display refunds table and summary
 */

import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Divider,
  CircularProgress,
  Alert,
} from "@mui/material";
import { format } from "date-fns";
import { processRefund } from "../services/refundService.js";
import RefundStatusBadge from "./RefundStatusBadge.jsx";
import RefundConfirmationModal from "./RefundConfirmationModal.jsx";
import { logError } from "../../../shared/utils/logger.js";

/**
 * RefundList Component
 * @param {Object} props
 * @param {Object} props.invoice - Invoice object
 * @param {Function} props.onRefundUpdated - Callback after refund update
 */
export const RefundList = ({ invoice, onRefundUpdated }) => {
  const [processDialog, setProcessDialog] = useState(false);
  const [selectedRefund, setSelectedRefund] = useState(null);
  const [updateData, setUpdateData] = useState({ status: "", notes: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [selectedRefundForConfirmation, setSelectedRefundForConfirmation] = useState(null);

  const refunds = invoice?.refunds || [];

  // Calculate refund summary
  const totalPaid =
    (invoice?.payments || []).reduce((sum, p) => sum + (p.amount || 0), 0) ||
    0;
  const totalRefunded =
    refunds
      .filter((r) => r.status !== "cancelled")
      .reduce((sum, r) => sum + (r.amount || 0), 0) || 0;
  const availableForRefund = Math.max(totalPaid - totalRefunded, 0);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-ZA", {
      style: "currency",
      currency: "ZAR",
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), "MMM dd, yyyy");
    } catch {
      return "Invalid date";
    }
  };

  const handleProcessRefund = (refund, index) => {
    setSelectedRefund({ ...refund, index });
    setUpdateData({
      status: refund.status === "pending" ? "processing" : refund.status,
      notes: refund.notes || "",
    });
    setProcessDialog(true);
    setError(null);
  };

  const handleUpdateRefund = async () => {
    if (!selectedRefund) return;

    const refundIndex = selectedRefund.index;
    const newStatus = updateData.status;

    try {
      setLoading(true);
      setError(null);

      const result = await processRefund(invoice.id, refundIndex, {
        status: newStatus,
        notes: updateData.notes || null,
      });

      if (result.success) {
        setProcessDialog(false);
        setUpdateData({ status: "", notes: "" });
        
        // If status is completed, show confirmation modal
        if (newStatus === "completed") {
          // Get updated refund from invoice
          const updatedRefund = invoice.refunds[refundIndex];
          setSelectedRefundForConfirmation({
            refund: updatedRefund,
            index: refundIndex,
          });
          setConfirmationModalOpen(true);
        } else {
          setSelectedRefund(null);
          onRefundUpdated?.();
        }
      } else {
        setError(result.error || "Failed to process refund");
      }
    } catch (err) {
      setError(err.message || "Failed to process refund");
      logError("Process refund error", { error: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDialog = () => {
    if (!loading) {
      setProcessDialog(false);
      setSelectedRefund(null);
      setUpdateData({ status: "", notes: "" });
      setError(null);
    }
  };

  if (refunds.length === 0) {
    return null;
  }

  return (
    <>
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Refunds
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {/* Refund Summary */}
          <Box sx={{ mb: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mb: 1,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Total Paid:
              </Typography>
              <Typography variant="body2">
                {formatCurrency(totalPaid)}
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mb: 1,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Total Refunded:
              </Typography>
              <Typography variant="body2" color="error.main">
                {formatCurrency(totalRefunded)}
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Available for Refund:
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {formatCurrency(availableForRefund)}
              </Typography>
            </Box>
          </Box>

          {/* Refunds Table */}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Amount</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>Method</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Processed By</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {refunds.map((refund, index) => (
                  <TableRow key={index}>
                    <TableCell>{formatCurrency(refund.amount)}</TableCell>
                    <TableCell>{formatDate(refund.date)}</TableCell>
                    <TableCell>{refund.reason}</TableCell>
                    <TableCell>
                      {refund.method?.replace("_", " ").toUpperCase() || "N/A"}
                    </TableCell>
                    <TableCell>
                      <RefundStatusBadge status={refund.status} />
                    </TableCell>
                    <TableCell>
                      {refund.processedBy || "-"}
                      {refund.processedAt && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          {formatDate(refund.processedAt)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {refund.status !== "completed" &&
                        refund.status !== "cancelled" && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleProcessRefund(refund, index)}
                          >
                            Process
                          </Button>
                        )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Process Refund Dialog */}
      <Dialog open={processDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Process Refund</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {selectedRefund && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Refund Amount: <strong>{formatCurrency(selectedRefund.amount)}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Reason: {selectedRefund.reason}
              </Typography>
            </Box>
          )}
          <TextField
            fullWidth
            select
            label="Status"
            value={updateData.status}
            onChange={(e) =>
              setUpdateData({ ...updateData, status: e.target.value })
            }
            sx={{ mt: 2 }}
          >
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="processing">Processing</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
            <MenuItem value="failed">Failed</MenuItem>
            <MenuItem value="cancelled">Cancelled</MenuItem>
          </TextField>
          <TextField
            fullWidth
            label="Notes"
            value={updateData.notes}
            onChange={(e) =>
              setUpdateData({ ...updateData, notes: e.target.value })
            }
            multiline
            rows={3}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleUpdateRefund}
            variant="contained"
            disabled={loading || !updateData.status}
          >
            {loading ? <CircularProgress size={20} /> : "Update Status"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Refund Confirmation Modal */}
      <RefundConfirmationModal
        open={confirmationModalOpen}
        onClose={() => {
          setConfirmationModalOpen(false);
          setSelectedRefundForConfirmation(null);
        }}
        invoice={invoice}
        refund={selectedRefundForConfirmation?.refund}
        refundIndex={selectedRefundForConfirmation?.index}
        onSent={() => {
          onRefundUpdated?.();
        }}
      />
    </>
  );
};

export default RefundList;

